import type { ReactNode } from 'react'

/**
 * The full-width action at the bottom of an unauthenticated form.
 *
 * Pill-shaped because the CarbonWeb system makes every call to action fully
 * rounded. `primary` is the thing the screen is for; `secondary` is an outlined
 * alternative of equal weight, used where the two choices are genuinely equal —
 * unsubscribe or keep receiving — and not for a lesser option.
 */

interface PillButtonProps {
  readonly type: 'button' | 'submit'
  readonly variant: 'primary' | 'secondary'
  readonly disabled?: boolean
  readonly onClick?: () => void
  readonly children: ReactNode
}

const VARIANTS = {
  primary: 'border border-transparent bg-brand text-brand-contrast hover:bg-brand-strong',
  secondary: 'border-2 border-brand bg-transparent text-brand hover:bg-brand-soft',
} as const

export function PillButton({ type, variant, disabled, onClick, children }: PillButtonProps) {
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      disabled={disabled}
      onClick={onClick}
      className={`w-full rounded-pill px-5 py-2.5 font-bold text-[14px] disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]}`}
    >
      {children}
    </button>
  )
}
